import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Description, Skeleton } from '@heroui/react';
import {
  dashboardKeys,
  getDashboardLeaderboard,
  type LeaderboardPeriod,
} from '../../../api/dashboard';
import { StatusCallout } from '../../../components/ui/status-callout';
import { useCurrentCondominium } from '../../../hooks/useCurrentCondominium';
import { LeaderboardCategoryCard } from './leaderboard-category-card';
import { PeriodToggle } from './period-toggle';

export function CondominiumRankingSection() {
  const { condominium, condominiums, isLoading: isCondominiumsLoading } = useCurrentCondominium();
  const condominiumId = condominium?.id ?? '';
  const [period, setPeriod] = useState<LeaderboardPeriod>('all');

  const leaderboardQuery = useQuery({
    queryKey: dashboardKeys.leaderboard(condominiumId, period),
    queryFn: () => getDashboardLeaderboard(condominiumId, period),
    enabled: !!condominiumId,
  });

  if (isCondominiumsLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-9 w-full max-w-md" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-56 w-full rounded-xl" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (condominiums.length === 0 || !condominiumId) {
    return (
      <Card>
        <Card.Content className="flex flex-col items-center gap-3 py-12 text-center">
          <Building2 size={36} className="text-muted" />
          <Description>
            Selecione um condomínio no menu do usuário para ver o ranking.
          </Description>
        </Card.Content>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-warning/15 text-lg"
          >
            🏆
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Placar do condomínio</p>
            <Description className="text-sm">
              Ranking dos moradores de <strong>{condominium?.name}</strong>
            </Description>
          </div>
        </div>
        <PeriodToggle period={period} onPeriodChange={setPeriod} />
      </div>

      {leaderboardQuery.isError ? (
        <StatusCallout
          status="danger"
          title="Erro ao carregar ranking"
          action={
            <Button size="sm" variant="secondary" onPress={() => leaderboardQuery.refetch()}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar o ranking. Tente novamente em instantes.
        </StatusCallout>
      ) : leaderboardQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-56 w-full rounded-xl" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(leaderboardQuery.data?.categories ?? []).map((category) => (
            <LeaderboardCategoryCard key={category.key} category={category} />
          ))}
        </div>
      )}
    </div>
  );
}
